import {
  CircularProgress,
  DialogActions,
  makeStyles,
  TextField,
} from '@material-ui/core';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import foundersAPI, { Founder, FounderId } from '../../api/founders';
import mentorsAPI, { Mentor, MentorId } from '../../api/mentors';
import venturesAPI from '../../api/ventures';
import { Venture, VentureId } from '../../api/ventures/types/Venture';
import Button from './button';
import ModalV2 from './modal-v2';
import SnackMessage from './snack-message';
import Text from './text';

const MAX_CHARS = 20000;
const TITLE_PROPS = { title: 'Enrich Profile' };

interface BaseProps {
  open: boolean;
  onClose: () => void;
}

type EnrichProfileModalProps =
  | (BaseProps & {
    type: 'mentor';
    entityId: MentorId;
    onSuccess: (mentor: Mentor) => void;
  })
  | (BaseProps & {
    type: 'founder';
    entityId: FounderId;
    onSuccess: (founder: Founder) => void;
  })
  | (BaseProps & {
    type: 'venture';
    entityId: VentureId;
    onSuccess: (venture: Venture) => void;
  });

const useStyles = makeStyles({
  actions: {
    padding: '32px 0 0',
  },
  textField: {
    marginTop: 16,
  },
});

function EnrichProfileModal(props: EnrichProfileModalProps) {
  const { open, onClose, type, entityId, onSuccess } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isOverLimit = text.length > MAX_CHARS;
  const isApplyDisabled = isLoading || !text.trim() || isOverLimit;

  const handleClose = useCallback(() => {
    setText('');
    onClose();
  }, [onClose]);

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
    },
    [],
  );

  const handleApply = useCallback(async () => {
    try {
      setIsLoading(true);
      switch (type) {
        case 'mentor': {
          const updatedMentor = await mentorsAPI.enrichProfile(entityId, text);
          onSuccess(updatedMentor);
          break;
        }
        case 'founder': {
          const updatedFounder = await foundersAPI.enrichProfile(entityId, text);
          onSuccess(updatedFounder);
          break;
        }
        case 'venture': {
          const updatedVenture = await venturesAPI.enrichProfile(entityId, text);
          onSuccess(updatedVenture);
          break;
        }
        default: {
          break;
        }
      }
      enqueueSnackbar('Profile enriched successfully', {
        variant: 'success',
      });
      setText('');
      onClose();
    } catch (e) {
      const messageError = (e as AxiosError).response?.data?.message;
      enqueueSnackbar(
        'An error occurred while enriching the profile. Please, try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
    } finally {
      setIsLoading(false);
    }
  }, [text, enqueueSnackbar, onClose, type, entityId, onSuccess]);

  return (
    <ModalV2
      width='600px'
      open={open}
      onClose={handleClose}
      titleProps={TITLE_PROPS}>
      <Text variant='normal'>
        Please paste text in free form and we will update the profile based on
        this data
      </Text>
      <TextField
        className={classes.textField}
        multiline
        rows={8}
        fullWidth
        variant='outlined'
        value={text}
        onChange={handleTextChange}
        error={isOverLimit}
        helperText={
          isOverLimit
            ? `${text.length.toLocaleString()} / ${MAX_CHARS.toLocaleString()} characters`
            : undefined
        }
        InputProps={{
          disabled: isLoading,
        }}
        data-testid='enrich-profile-text'
      />
      <DialogActions className={classes.actions}>
        <Button
          variant='outlined'
          onClick={handleClose}
          data-testid='enrich-profile-cancel'
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={isApplyDisabled}
          data-testid='enrich-profile-apply'
        >
          {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Apply'}
        </Button>
      </DialogActions>
    </ModalV2>
  );
}

export default EnrichProfileModal;
